import React, { useState } from 'react';

import { Tooltip, useMediaQuery, useTheme } from '@material-ui/core';

import { UiButton } from '../../../../ui/ui-button';
import { UiPhoneTextField } from '../../../../ui/ui-phone-text-field';

import { employeeNameFromSimpleContact } from '../../../../employees/utils/employee-name';
import { formatPhoneNumber } from '../../../../util/formatters';
import { isPhoneNumber } from '../../../../util/string-validators';

type InputProps = {
  messageDetail: AnnouncementDeliverabilityDetail;
  updatePhoneNumber: (employeeId: number, phoneNumber: string) => void;
  retryMessage: (handleRetrySendMessage: AnnouncementDeliverabilityDetail) => Promise<boolean>;
};

export const MessageDeliveryDetailsEditableRow = (props: InputProps) => {
  const { messageDetail, updatePhoneNumber, retryMessage } = props;

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState<string>(messageDetail.phoneNumber || '');
  const [messageResent, setMessageResent] = useState<boolean>(false);

  const theme = useTheme();
  const isOnMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const handleSavePhoneNumber = () => {
    updatePhoneNumber(messageDetail.employeeId, newPhoneNumber);
    // If we retried but then changed the phone number, we want to be able to retry again
    setMessageResent(false);
    setIsEditing(false);
  };

  const handleRetrySendMessage = async () => {
    const sentMessage = await retryMessage(messageDetail);
    setMessageResent(sentMessage);
  };

  const handleClickEdit = () => {
    setIsEditing(true);
  };

  const handleStopEdit = () => {
    setIsEditing(false);
  };

  const handleChangePhoneNumber = (phoneNumber: string | null) => {
    setNewPhoneNumber(phoneNumber || '');
  };

  const tooltipTitle = () => {
    if (!messageDetail.phoneNumber) {
      return 'Message was not delivered because phone number is missing.';
    }
    if (isPhoneNumber(messageDetail.phoneNumber)) {
      return 'Message was not delivered because phone was off, out of cell range, or is a landline.';
    }
    return 'Message failed to send. Phone number may be invalid.';
  };

  const phoneNumberToDisplay = newPhoneNumber || messageDetail.phoneNumber;

  return (
    <div
      className={`${
        isOnMobile ? 'u-height-80px' : 'u-height-40px'
      } u-display-flex u-justifyContent-spaceBetween u-alignItems-center`}
    >
      <div className="u-width-40">
        <Tooltip title={tooltipTitle() || ''}>
          <span>
            {employeeNameFromSimpleContact({
              nickname: messageDetail.nickname,
              firstName: messageDetail.firstName,
              lastName: messageDetail.lastName,
            })}
          </span>
        </Tooltip>
      </div>
      {isEditing ? (
        <>
          <div className="u-width-40 u-alignSelf-center">
            <UiPhoneTextField
              size="small"
              label="Phone Number"
              value={newPhoneNumber}
              onChange={handleChangePhoneNumber}
            />
          </div>
          <div className="u-width-30 u-textAlign-right">
            <UiButton
              className={isOnMobile ? '' : 'u-margin-right-lg'}
              size="sm"
              variant="tertiary"
              onClick={handleStopEdit}
            >
              Cancel
            </UiButton>
            <UiButton
              size="sm"
              variant="primary"
              onClick={handleSavePhoneNumber}
              disabled={messageDetail.phoneNumber === newPhoneNumber}
            >
              Save
            </UiButton>
          </div>
        </>
      ) : (
        <>
          <div className="u-width-30">
            {formatPhoneNumber(phoneNumberToDisplay) ?? phoneNumberToDisplay}
          </div>
          <div className="u-width-30 u-textAlign-right">
            <UiButton
              onClick={handleClickEdit}
              size="sm"
              variant="tertiary"
              className="u-margin-right-lg"
            >
              Edit
            </UiButton>
            <UiButton
              disabled={messageResent}
              size="sm"
              variant="tertiary"
              onClick={handleRetrySendMessage}
            >
              Retry
            </UiButton>
          </div>
        </>
      )}
    </div>
  );
};
